/**
 * "Create Well Servicing Job" on an accepted Well Servicing quote (WS-5,
 * fieldiq-api#1295, POST /quotes/:id/create-ws-job).
 *
 * Well Testing's Create Well Testing Job, plus the SCOPES a Well Servicing job
 * must carry (ws_jobs needs at least one — workover, rod pull, … from
 * GET /well-servicing/meta's jobScopes) and an optional rig. The well list is
 * the quote customer's wells (GET /well-servicing/wells?customerId=). The day
 * is optional and becomes the job's planned start.
 *
 * A press with no well, or no scope, is SAID, not sent: the API's WELL_REQUIRED
 * / SCOPES_REQUIRED sentence, in the quote screen's refusal notice. Every API
 * refusal goes the same way (wsQuoteRefusal), and success hands the job to the
 * screen, which opens it — a repeat (alreadyCreated) opens the job that
 * already exists.
 *
 * Shown by QuoteDetailScreen only on an accepted Well Servicing quote, only to
 * admin and office staff (the door's requireRole), and only once
 * created-records says the quote has no Well Servicing job yet.
 */
import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle, Loader2 } from 'lucide-react';
import { api } from '../../api/client';
import { useWsMeta, useWsRigs, useWsWells } from '../../hooks/useWellServicing';
import { wsQuoteRefusal } from '../../lib/ws-quotes';
import type { CreateWsJobResponse } from '../../types/quote-pricing';

const SELECT = 'bg-fieldiq-dark border border-fieldiq-border rounded-lg px-2 py-1.5 text-sm text-white max-w-[16rem]';
const ACTION_PRIMARY = 'fi-action fi-action--standard fi-action--primary disabled:opacity-50 disabled:cursor-not-allowed';

export function CreateWsJobAction({
  quoteId,
  customerId,
  onCreated,
  onRefused,
}: {
  quoteId: string;
  customerId: string | null | undefined;
  onCreated: (data: CreateWsJobResponse['data']) => void;
  onRefused: (refusal: { code?: string; error: string }) => void;
}) {
  const [wellId, setWellId] = useState('');
  const [scopes, setScopes] = useState<string[]>([]);
  const [rigId, setRigId] = useState('');
  const [day, setDay] = useState('');

  const create = useMutation({
    mutationFn: () => api.quotes.createWsJob(quoteId, {
      wellId,
      scopes,
      ...(rigId ? { rigId } : {}),
      ...(day ? { scheduledDate: day } : {}),
    }),
    onSuccess: (res) => {
      const d = res.data?.data;
      if (d?.job?.id) onCreated(d);
    },
    onError: (err) => onRefused(wsQuoteRefusal(err, 'Failed to create the Well Servicing job')),
  });

  const press = () => {
    // No customer on the quote: the API's QUOTE_CUSTOMER_REQUIRED is the reason, so ask it.
    if (customerId && !wellId) {
      onRefused(wsQuoteRefusal({ response: { status: 400, data: { code: 'WELL_REQUIRED' } } }, ''));
      return;
    }
    if (customerId && scopes.length === 0) {
      onRefused(wsQuoteRefusal({ response: { status: 400, data: { code: 'SCOPES_REQUIRED' } } }, ''));
      return;
    }
    create.mutate();
  };

  return (
    <span className="inline-flex flex-wrap items-center gap-2" data-testid="create-ws-job">
      {customerId && <WellPicker customerId={customerId} wellId={wellId} onWell={setWellId} />}
      {customerId && <ScopePicker scopes={scopes} onScopes={setScopes} />}
      {customerId && <RigPicker rigId={rigId} onRig={setRigId} />}
      <input
        type="date"
        aria-label="Well Servicing job planned start (optional)"
        value={day}
        onChange={(e) => setDay(e.target.value)}
        className="bg-fieldiq-dark border border-fieldiq-border rounded-lg px-2 py-1.5 text-sm text-white"
      />
      <button onClick={press} disabled={create.isPending} className={ACTION_PRIMARY}>
        {create.isPending ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle size={15} />}
        Create Well Servicing Job
      </button>
    </span>
  );
}

/** The quote customer's wells. Mounted only with a customer. */
function WellPicker({ customerId, wellId, onWell }: { customerId: string; wellId: string; onWell: (id: string) => void }) {
  const wells = useWsWells({ customerId });
  const rows = wells.data?.data ?? [];
  const empty = wells.isLoading ? 'Loading wells…' : wells.isError ? 'Wells could not be loaded' : rows.length ? 'Choose the well…' : 'No wells for this customer';
  return (
    <select aria-label="Well Servicing job well" value={wellId} onChange={(e) => onWell(e.target.value)} className={SELECT}>
      <option value="">{empty}</option>
      {rows.map((w) => {
        const ident = w.uwi || w.apiNumber;
        return (
          <option key={w.id} value={w.id}>
            {w.wellName}{ident ? ` (${ident})` : ''}
          </option>
        );
      })}
    </select>
  );
}

/** The job's scopes of work, from the API's catalog (meta.jobScopes), as toggles. At least one is required. */
function ScopePicker({ scopes, onScopes }: { scopes: string[]; onScopes: (next: string[]) => void }) {
  const meta = useWsMeta();
  const catalog = meta.data?.data?.jobScopes ?? [];
  if (meta.isLoading) return <span className="text-sm text-fieldiq-muted">Loading scopes…</span>;
  if (catalog.length === 0) return <span className="text-sm text-fieldiq-muted">{meta.isError ? 'Scopes could not be loaded' : 'No scopes of work'}</span>;
  return (
    <span role="group" aria-label="Well Servicing job scopes" className="inline-flex flex-wrap gap-1.5">
      {catalog.map((s) => {
        const on = scopes.includes(s.value);
        return (
          <label
            key={s.value}
            className={`inline-flex cursor-pointer items-center gap-1.5 rounded-lg border px-2 py-1 text-xs ${on ? 'border-fi-amber bg-fi-amber/15 text-white' : 'border-fieldiq-border text-fieldiq-text'}`}
          >
            <input
              type="checkbox"
              className="accent-amber-500"
              checked={on}
              onChange={() => onScopes(on ? scopes.filter((x) => x !== s.value) : [...scopes, s.value])}
            />
            {s.label}
          </label>
        );
      })}
    </span>
  );
}

/** The rig, optional — a job can be booked before one is named. */
function RigPicker({ rigId, onRig }: { rigId: string; onRig: (id: string) => void }) {
  const rigs = useWsRigs();
  const rows = rigs.data?.data ?? [];
  return (
    <select aria-label="Well Servicing job rig (optional)" value={rigId} onChange={(e) => onRig(e.target.value)} className={SELECT}>
      <option value="">{rigs.isLoading ? 'Loading rigs…' : rigs.isError ? 'Rigs could not be loaded' : 'Rig: not yet'}</option>
      {rows.map((r) => (
        <option key={r.id} value={r.id}>
          {r.rigNumber}{r.equipmentName ? ` · ${r.equipmentName}` : ''}
        </option>
      ))}
    </select>
  );
}
